import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useLocation, useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { ItemChecklistEntrega } from "../../domain/models/item_checklist_entrega";
import { ChecklistItemEntregaCadastroForm, ChecklistItemEntregaCadastroSchema, defaultChecklistItemEntregaCadastroForm } from "../schemas/checklist_entrega_lista/checklistItemEntregaCadastro";
import { useItensChecklistEntregaListaPagination } from "../hooks/checklist_entrega_lista_itens/pagination";
import { useItensChecklistEntregaListaFilters } from "../hooks/checklist_entrega_lista_itens/filters";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChecklistEntregaAmbienteModel } from "../../domain/models/checklist_entrega_ambiente";
import { ChecklistEntrega } from "../../domain/models/checklist_entrega";
import { GridRowSelectionModel } from "@mui/x-data-grid";

type LoadItensChecklistEntregaListaState = {
    ordem?: string,
    descricao?: string,

    currentPage?: number;
    rowsPerPage?: number;
}

export const useChecklistEntregaItensCadastroViewModel = () => {
    const {
        entregaObras: {
            createItemChecklistEntregaUseCase,
            updateItemChecklistEntregaUseCase,
            deleteItemChecklistEntregaUseCase,
            getAllItemChecklistEntregaUseCase,
            getItemChecklistEntregaByIdUseCase,
            getChecklistEntregaByIdUseCase,
            getChecklistEntregaAmbienteByIdUseCase,
        },
        commons: {
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
        }
    } = useDependencyInjection();

    const location = useLocation();
    const navigate = useNavigate();

    const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const debouncedRef = useCreateDebounceHandler(1500);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const itensFilters = useItensChecklistEntregaListaFilters();

    const pagination = useItensChecklistEntregaListaPagination();
    const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>();

    //itens checklist entrega
    const [openCadastroItemModal, setOpenCadastroItemModal] = useState(false);
    const [openDeleteItemModal, setOpenDeleteItemModal] = useState(false);
    const [selectedIdItemChecklistEntregaDelete, setSelectedIdItemChecklistEntregaDelete] = useState<number>();
    const [selectedIdChecklistEntregaAmbiente, setSelectedIdChecklistEntregaAmbiente] = useState<number>();
    const [selectedIdChecklistEntrega, setSelectedIdChecklistEntrega] = useState<number>();

    const [selectedChecklistEntregaAmbiente, setSelectedChecklistEntregaAmbiente] = useState<ChecklistEntregaAmbienteModel | undefined>();
    const [selectedChecklistEntrega, setSelectedChecklistEntrega] = useState<ChecklistEntrega | undefined>();

    const [itemChecklistEntregaLista, setItemChecklistEntregaLista] = useState<ItemChecklistEntrega[]>();
    const [selectedItemChecklistEntrega, setSelectedItemChecklistEntrega] = useState<ItemChecklistEntrega | undefined>();

    const hookForm = useForm<ChecklistItemEntregaCadastroForm>({
        mode: "onSubmit",
        reValidateMode: "onSubmit",
        defaultValues: defaultChecklistItemEntregaCadastroForm,
        resolver: zodResolver(ChecklistItemEntregaCadastroSchema),
    });

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification('Erro', [e]),
    })

    //#region CARREGAMENTO.
    const loadAllItensChecklistEntregaLista = useCallback(async (idChecklistEntregaAmbiente: number, funcState?: LoadItensChecklistEntregaListaState) => {
        try {
            setLoading(true);
            const filteredOrdem = evaluateParamValue(funcState?.ordem, itensFilters.ordem)
            const filteredDescricao = evaluateParamValue(funcState?.descricao, itensFilters.descricao)
            const filteredPage = funcState?.currentPage ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllItemChecklistEntregaUseCase.execute(
                idChecklistEntregaAmbiente,
                filteredOrdem ?? undefined,
                filteredDescricao ?? undefined,

                filteredPage,
                filteredRowsPerPage,
            );
            pagination.setRowsPerPage(getAllItemChecklistEntregaUseCase.pageSize);
            pagination.setTotalItems(getAllItemChecklistEntregaUseCase.totalItems);

            setItemChecklistEntregaLista(getAllItemChecklistEntregaUseCase.items);
        } catch (e: unknown) {
            setItemChecklistEntregaLista([]);
            pushErrorNotification("Error", processError(e, "Falha ao buscar lista de itens do checklist de entrega!"));
        } finally {
            setLoading(false);
        }
    }, [
        getAllItemChecklistEntregaUseCase,
        itensFilters,
        pagination,
        pushErrorNotification
    ]);


    const loadItemChecklistEntregaById = useCallback(async (idItemChecklistEntrega: number) => {
        try {
            setPageLoading(true);
            const checklist = await getItemChecklistEntregaByIdUseCase.execute(idItemChecklistEntrega);
            hookForm.setValue('idItemChecklistEntrega', checklist.idItemChecklistEntrega);
            hookForm.setValue('ordem', checklist.ordem ?? '');
            hookForm.setValue('descricao', checklist.descricao ?? '');

            setSelectedItemChecklistEntrega(checklist);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar checklist de entrega!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getItemChecklistEntregaByIdUseCase,
        hookForm,
        setPageLoading,
        pushErrorNotification]);


    const loadChecklistEntregaInformacoes = useCallback(async (idChecklistEntregaAmbiente: number) => {
        try {
            setPageLoading(true);
            const ambiente = await getChecklistEntregaAmbienteByIdUseCase.execute(idChecklistEntregaAmbiente);
            if (!ambiente) {
                pushErrorNotification('Error', 'Falha ao buscar checklist de entrega, ambiente não encontrado!', { isAutoFinish: true });
            }
            setSelectedChecklistEntregaAmbiente(ambiente);
            setSelectedIdChecklistEntrega(ambiente.idChecklistEntrega);
            const checklist = await getChecklistEntregaByIdUseCase.execute(ambiente.idChecklistEntrega);
            if (!checklist) {
                pushErrorNotification('Error', 'Falha ao buscar checklist de entrega, checklist não encontrado!', { isAutoFinish: true });
            }
            setSelectedChecklistEntrega(checklist);

        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar checklist de entrega!'));
        } finally {
            setPageLoading(false);
        }
    }, [
        getChecklistEntregaAmbienteByIdUseCase,
        getChecklistEntregaByIdUseCase,
        setPageLoading,
        pushErrorNotification
    ]);

    //#endregion




    //#region PAGE START
    usePageStartBootstrap(async () => {
        try {
            const params = new URLSearchParams(location.search);
            const locationId = params.get("id");
            const id = locationId ?? undefined;

            if (!id || id.length === 0) {
                setErrorMessage("Falha ao recuperar id do checklist!");
                return;
            }
            setSelectedIdChecklistEntregaAmbiente(Number(id));
            await loadChecklistEntregaInformacoes(Number(id));
            await loadAllItensChecklistEntregaLista(Number(id));
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar dados das páginas!")[0]);

        }
    });


    //#endregion




    //#region NOVO/EDITAR.

    const handleItemChecklistEntregaOpenState = useCallback(async (idItemChecklistEntrega: number | null, openState: boolean) => {
        setOpenCadastroItemModal(openState);
        try {
            if (openState) {
                if (idItemChecklistEntrega) {
                    await loadItemChecklistEntregaById(idItemChecklistEntrega);
                } else {
                    setSelectedItemChecklistEntrega(undefined);
                }
            } else {
                setSelectedItemChecklistEntrega(undefined);
            }
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao iniciar cadastro de item do checklist de entrega!'));
        }
    }, [
        loadItemChecklistEntregaById,
        pushErrorNotification,
    ]);

    const handleSubmitItemChecklistEntrega = useCallback(async (form: ChecklistItemEntregaCadastroForm) => {
        try {
            setSaving(true);
            if (form.idItemChecklistEntrega) {
                await updateItemChecklistEntregaUseCase.execute(
                    form.idItemChecklistEntrega,
                    form.descricao,
                    form.ordem,
                );
                await loadItemChecklistEntregaById(form.idItemChecklistEntrega!);
                pushInfoNotification("Sucesso", "item do Checklist atualizado com sucesso!", { isAutoFinish: true });
                setOpenCadastroItemModal(false);
            } else {
                if (!selectedIdChecklistEntregaAmbiente) {
                    pushErrorNotification("Erro", "Não foi possivel criar item do Checklist de entrega, verifique se o checklist de entrega está selecionado!", { isAutoFinish: true });
                    return;
                }
                const idNewItem = await createItemChecklistEntregaUseCase.execute(
                    selectedIdChecklistEntregaAmbiente!,
                    form.descricao,
                    form.ordem,
                );
                pushInfoNotification("Sucesso", "item do Checklist criado com sucesso!", { isAutoFinish: true });
                await loadItemChecklistEntregaById(idNewItem);
                setOpenCadastroItemModal(false);
            }

            await loadAllItensChecklistEntregaLista(selectedIdChecklistEntregaAmbiente!);
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar item do Checklist de entrega!"));
        } finally {
            setSaving(false);
        }
    }, [
        selectedIdChecklistEntregaAmbiente,
        createItemChecklistEntregaUseCase,
        updateItemChecklistEntregaUseCase,
        loadItemChecklistEntregaById,
        loadAllItensChecklistEntregaLista,
        pushErrorNotification,
        pushInfoNotification
    ]);


    const handleEditChecklistEntregaItem = useCallback(async (idItemChecklistEntrega: number) => {
        try {
            const itemChecklist = itemChecklistEntregaLista?.find(item => item.idItemChecklistEntrega === idItemChecklistEntrega);
            hookForm.setValue('idItemChecklistEntrega', itemChecklist?.idItemChecklistEntrega);
            hookForm.setValue('ordem', itemChecklist?.ordem ?? '');
            hookForm.setValue('descricao', itemChecklist?.descricao ?? '');
            if (itemChecklist !== undefined) {
                setSelectedItemChecklistEntrega(itemChecklist);
                setOpenCadastroItemModal(true);
            } else {
                pushWarnNotification("Aviso", "Item do checklist de entrega não encontrado na lista!", { isAutoFinish: true });
            }
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao iniciar edição do item do checklist de entrega!'));
        }
    }, [itemChecklistEntregaLista, hookForm, pushErrorNotification, pushWarnNotification]);


    const handleNewItemChecklistEntrega = useCallback(async () => {
        hookForm.reset(defaultChecklistItemEntregaCadastroForm);
        setSelectedItemChecklistEntrega(undefined);
        setOpenCadastroItemModal(true);
    }, [hookForm]);

    //#endregion





    //#region Reload/Pageback
    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            if (!selectedIdChecklistEntregaAmbiente) {
                navigate(`/ChecklistEntregaAmbiente_lista?id=${selectedIdChecklistEntregaAmbiente}`);
                return;
            }

            await loadAllItensChecklistEntregaLista(selectedIdChecklistEntregaAmbiente);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [
        selectedIdChecklistEntregaAmbiente,
        navigate,
        loadAllItensChecklistEntregaLista,
        pushErrorNotification
    ]);

    const handleBack = useCallback(() => {
        navigate(`/ChecklistEntregaAmbiente_lista?id=${selectedIdChecklistEntrega}`);
    }, [navigate, selectedIdChecklistEntrega]);

    //#endregion



    //#region PAGINAÇÃO ITENS.

    const handlePageChange = useCallback(async (page: number) => {
        try {
            if (!selectedIdChecklistEntregaAmbiente) {
                return
            }
            setLoading(true);
            pagination.setPage(page + 1);
            await loadAllItensChecklistEntregaLista(selectedIdChecklistEntregaAmbiente, { currentPage: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, selectedIdChecklistEntregaAmbiente, loadAllItensChecklistEntregaLista, pushErrorNotification]);

    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);

            if (!selectedIdChecklistEntregaAmbiente) {
                return
            }
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadAllItensChecklistEntregaLista(selectedIdChecklistEntregaAmbiente, { rowsPerPage, currentPage: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, selectedIdChecklistEntregaAmbiente, loadAllItensChecklistEntregaLista, pushErrorNotification]);



    //#endregion




    //#region DELECAO 
    const handleItemChecklistEntregaDeleteOpenState = useCallback((idItemChecklistEntrega: number | null, openState: boolean) => {
        setOpenDeleteItemModal(openState);
        if (openState) {
            if (idItemChecklistEntrega) {
                setSelectedIdItemChecklistEntregaDelete(idItemChecklistEntrega);
            } else {
                setSelectedIdItemChecklistEntregaDelete(undefined);
            }
        }
    }, [setOpenDeleteItemModal, setSelectedIdItemChecklistEntregaDelete]);


    // const handleConfirmarDeletarItemChecklistEntrega = useCallback(async () => {
    //     try {
    //         setSaving(true);
    //         if (selectedIdItemChecklistEntregaDelete) {
    //             await deleteItemChecklistEntregaUseCase.execute(selectedIdItemChecklistEntregaDelete);
    //             pushInfoNotification("Sucesso", " Item do Checklist de entrega deletado com sucesso!", { isAutoFinish: true });
    //         } else {
    //             pushErrorNotification("Erro", "Não foi possivel deletar Item do Checklist de entrega!", { isAutoFinish: true });
    //         }

    //         if (selectedIdChecklistEntregaAmbiente) {
    //             await loadAllItensChecklistEntregaLista(selectedIdChecklistEntregaAmbiente);
    //         } else {
    //             setItemChecklistEntregaLista([]);
    //         }

    //     } catch (e: unknown) {
    //         pushErrorNotification("Erro", processError(e, "Falha ao Deletar Item do Checklist de entrega!"));
    //     } finally {
    //         setSaving(false);
    //         setOpenDeleteItemModal(false);
    //         setSelectedIdItemChecklistEntregaDelete(undefined)
    //     }
    // }, [
    //     selectedIdItemChecklistEntregaDelete,
    //     deleteItemChecklistEntregaUseCase,
    //     selectedIdChecklistEntregaAmbiente,
    //     loadAllItensChecklistEntregaLista,
    //     setItemChecklistEntregaLista,
    //     pushErrorNotification,
    //     pushInfoNotification]);

    const handleConfirmarDeletarItemChecklistEntrega = useCallback(async () => {
        try {
            setSaving(true);
            if (selectedIdItemChecklistEntregaDelete) {
                await deleteItemChecklistEntregaUseCase.execute(selectedIdItemChecklistEntregaDelete);
                pushInfoNotification("Sucesso", " Item do Checklist de entrega deletado com sucesso!", { isAutoFinish: true });
            } else {
                if (rowSelectionModel) {
                    const idItens: number[] = [];
                    for (let row of rowSelectionModel.ids.values()) {
                        if (typeof row === 'number') {
                            idItens.push(row);
                        } else if (typeof row === 'string') {
                            try {
                                idItens.push(Number(row));
                            } catch (e) {
                            }
                        }
                    }
                    await Promise.all(
                        idItens.map(id =>
                            deleteItemChecklistEntregaUseCase.execute(id)
                        )
                    );
                    pushInfoNotification("Sucesso", "Itens do Checklist de entrega deletados com sucesso!", { isAutoFinish: true });
                }
            }

            if (selectedIdChecklistEntregaAmbiente) {
                await loadAllItensChecklistEntregaLista(selectedIdChecklistEntregaAmbiente);
            } else {
                setItemChecklistEntregaLista([]);
            }

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao Deletar Itens do Checklist de entrega!"));
        } finally {
            setSaving(false);
            setOpenDeleteItemModal(false);
            if (selectedIdChecklistEntregaAmbiente) {
                await loadAllItensChecklistEntregaLista(selectedIdChecklistEntregaAmbiente);
            }
            setRowSelectionModel({ type: 'include', ids: new Set() });
        }
    }, [
        selectedIdItemChecklistEntregaDelete,
        deleteItemChecklistEntregaUseCase,
        selectedIdChecklistEntregaAmbiente,
        rowSelectionModel,
        loadAllItensChecklistEntregaLista,
        setItemChecklistEntregaLista,
        pushErrorNotification,
        pushInfoNotification,
        setRowSelectionModel
    ]);

    //#endregion




    //#region PESQUISA ITENS.

    const handleItemOrdemChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {
            itensFilters.setOrdem(newValue ?? undefined);
            return async () => {
                try {
                    if (!selectedIdChecklistEntregaAmbiente) {
                        pushErrorNotification("Erro", "Seleciona um checklist de entrega!")
                        return
                    }

                    setLoading(true);
                    pagination.setPage(1);
                    await loadAllItensChecklistEntregaLista(selectedIdChecklistEntregaAmbiente
                        , { ordem: newValue ?? undefined, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar itens do checklist de entrega!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [
            itensFilters,
            pagination,
            selectedIdChecklistEntregaAmbiente,
            loadAllItensChecklistEntregaLista,
            pushErrorNotification
        ]);

    const handleItemDescricaoChange = useDebouncedCallback(
        debouncedRef,
        (newValue: string | null) => {

            itensFilters.setDescricao(newValue ?? undefined);
            return async () => {
                try {
                    if (!selectedIdChecklistEntregaAmbiente) {
                        pushErrorNotification("Erro", "Seleciona um checklist de entrega!")
                        return
                    }
                    setLoading(true);
                    pagination.setPage(1);
                    await loadAllItensChecklistEntregaLista(selectedIdChecklistEntregaAmbiente
                        , { descricao: newValue ?? undefined, currentPage: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Erro", processError(e, "Falha ao buscar itens do checklist de entrega!"));
                } finally {
                    setLoading(false);
                }
            };
        },
        [
            itensFilters,
            pagination,
            selectedIdChecklistEntregaAmbiente,
            loadAllItensChecklistEntregaLista,
            pushErrorNotification]
    );

    const handleItemClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            itensFilters.clearFilters();
            pagination.setPage(1);
            if (!selectedIdChecklistEntregaAmbiente) {
                pushErrorNotification("Erro", "Seleciona um checklist de entrega!")
                return
            }
            await loadAllItensChecklistEntregaLista(selectedIdChecklistEntregaAmbiente, {
                ordem: '',
                descricao: '',
                currentPage: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar itens do checklist de entrega!'));
        } finally {
            setLoading(false);
        }
    }, [
        itensFilters,
        pagination,
        selectedIdChecklistEntregaAmbiente,
        loadAllItensChecklistEntregaLista,
        pushErrorNotification]);


    //#endregion 

    //#region Checkbox Selection
    const handleRowSelectionModelChange = useCallback((rows: GridRowSelectionModel) => {
        setRowSelectionModel(rows);
    }, []);



    //#endregion 


    //#region History

    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (selectedIdChecklistEntregaAmbiente) {
                changeHistory.handleOpenChangeHistory(selectedIdChecklistEntregaAmbiente, LogTipoTabela.CHECKLIST_ENTREGA, true);
            }
        }
    }, [
        selectedIdChecklistEntregaAmbiente,
        changeHistory,
    ]);

    //#endregion

    return {
        loading,
        saving,
        errorMessage,
        pagination,
        selectedIdChecklistEntregaAmbiente,
        changeHistory,
        openCadastroItemModal,
        openDeleteItemModal,
        itemChecklistEntregaLista: itemChecklistEntregaLista ?? [],
        selectedItemChecklistEntrega,
        itensFilters,
        hookForm,
        checklistEntregaAmbiente: selectedChecklistEntregaAmbiente,
        checklistEntrega: selectedChecklistEntrega,
        rowSelectionModel,

        handleBack,
        handleRowSelectionModelChange,
        handlePageChange,
        handleRowsPerPageChange,
        handleItemOrdemChange,
        handleItemDescricaoChange,
        handleItemClearFilters,
        handleItemChecklistEntregaOpenState,
        handleSubmitItemChecklistEntrega,
        handleEditChecklistEntregaItem,
        handleItemChecklistEntregaDeleteOpenState,
        handleNewItemChecklistEntrega,
        handleConfirmarDeletarItemChecklistEntrega,
        handleOpenChangeHistory,
        handleReload,
    };
}


